/**
 * Hyperliquid Account Data Adapter
 *
 * Implements PerpsAccountAdapter by wrapping HyperliquidInfoClient.
 * Normalizes HL-specific account data (positions, balance, orders, fills)
 * into provider-agnostic Perps types.
 */

import { HyperliquidInfoClient } from "../client";
import type {
  PerpsPosition,
  PerpsBalance,
  PerpsOpenOrder,
  PerpsFill,
} from "../types/account";
import type { PerpsAccountAdapter } from "./types";

// ============================================================================
// Adapter Class
// ============================================================================

export class HyperliquidAccount implements PerpsAccountAdapter {
  private restClient: HyperliquidInfoClient;

  constructor(
    testnet = false,
    dexNames?: string[],
    sharedClient?: HyperliquidInfoClient,
  ) {
    this.restClient =
      sharedClient ?? new HyperliquidInfoClient({ testnet, dexNames });
  }

  async fetchPositions(address: string): Promise<PerpsPosition[]> {
    const state = await this.restClient.getClearinghouseState(address);
    return state.assetPositions
      .filter((w) => parseFloat(w.position.szi) !== 0)
      .map((w) => {
        const p = w.position;
        const szi = parseFloat(p.szi);
        return {
          symbol: p.coin,
          side: (szi > 0 ? "long" : "short") as "long" | "short",
          size: Math.abs(szi).toString(),
          entryPrice: p.entryPx,
          markPrice: "0", // Will be enriched by UI from mark price stream
          unrealizedPnl: p.unrealizedPnl,
          leverage: p.leverage.value.toString(),
          leverageType: p.leverage.type,
          liquidationPrice: p.liquidationPx,
          marginUsed: p.marginUsed,
          returnOnEquity: p.returnOnEquity,
        };
      });
  }

  async fetchBalance(address: string): Promise<PerpsBalance | null> {
    const state = await this.restClient.getClearinghouseState(address);
    const ms = state.crossMarginSummary;
    const accountValue = parseFloat(ms.accountValue);
    const totalRawUsd = parseFloat(ms.totalRawUsd);
    const marginUsed = parseFloat(ms.totalMarginUsed);
    const unrealizedPnl = accountValue - totalRawUsd;

    return {
      asset: "USDC",
      total: ms.accountValue,
      available: state.withdrawable,
      unrealizedPnl: unrealizedPnl.toFixed(2),
      marginUsed: marginUsed.toFixed(2),
    };
  }

  async fetchOpenOrders(address: string): Promise<PerpsOpenOrder[]> {
    const hlOrders = await this.restClient.getOpenOrders(address);
    return hlOrders.map((o) => ({
      id: o.oid.toString(),
      symbol: o.coin,
      side: (o.side === "B" ? "buy" : "sell") as "buy" | "sell",
      type: o.orderType,
      price: o.px,
      size: o.sz,
      filledSize: "0",
      createdAt: o.timestamp,
      reduceOnly: false,
      clientOrderId: o.cloid,
    }));
  }

  async fetchTradeHistory(address: string): Promise<PerpsFill[]> {
    const hlFills = await this.restClient.getUserFills(address);
    return hlFills.map((f) => ({
      id: f.tid.toString(),
      symbol: f.coin,
      side: (f.side === "B" ? "buy" : "sell") as "buy" | "sell",
      price: f.px,
      size: f.sz,
      fee: f.fee,
      realizedPnl: f.closedPnl,
      time: f.time,
      isMaker: !f.crossed,
      orderId: f.oid,
    }));
  }
}
